import { Input } from "@mcpjam/design-system/input";
import { Label } from "@mcpjam/design-system/label";
import { useState } from "react";

interface PassCriteriaSelectorProps {
  minimumPassRate: number;
  onMinimumPassRateChange: (rate: number) => void;
  /**
   * Suppress the inline "Minimum accuracy:" label so the caller can
   * provide its own row label (e.g. the suite settings sheet's
   * label/control split).
   */
  hideLabel?: boolean;
}

export function PassCriteriaSelector({
  minimumPassRate,
  onMinimumPassRateChange,
  hideLabel = false,
}: PassCriteriaSelectorProps) {
  const [editedValue, setEditedValue] = useState(minimumPassRate.toString());

  const handleBlur = () => {
    const numValue = Number(editedValue);
    if (!isNaN(numValue)) {
      const clampedValue = Math.max(0, Math.min(100, numValue));
      onMinimumPassRateChange(clampedValue);
      setEditedValue(clampedValue.toString());
    } else {
      // Reset to current value if invalid
      setEditedValue(minimumPassRate.toString());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleBlur();
      (e.target as HTMLInputElement).blur();
    } else if (e.key === "Escape") {
      setEditedValue(minimumPassRate.toString());
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div className="flex items-center gap-2">
      {hideLabel ? null : (
        <Label
          htmlFor="pass-criteria"
          className="text-sm text-muted-foreground"
        >
          Minimum accuracy:
        </Label>
      )}
      <Input
        id="pass-criteria"
        type="number"
        min={0}
        max={100}
        value={editedValue}
        onChange={(e) => setEditedValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="w-16 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <span className="text-sm text-muted-foreground">%</span>
    </div>
  );
}
